"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Spinner,
  Button,
  FormGroup,
  Input,
  Label,
  Alert,
} from "reactstrap"
import { FaEnvelope, FaPhone, FaIdCard, FaBuilding, FaCalendar, FaExclamationTriangle, FaCopy } from "react-icons/fa"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { PrintOrder } from "./badge-request"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface OrderedStaff {
  id: number
  person: {
    first_name: string
    last_name: string
    email: string
    phone: string
    id_doc_number: string
  }
  role: string
  exhibitor_name: string
  exhibition_title: string
  exhibition_id: number
  participation_id: number
  order_number: string
  order_date: string
  badge_reprint_price: number
  currency: string
  has_original_badge: boolean
  total_badges_printed: number
  last_badge_type: string
  last_printed_at: string | null
}

interface Participation {
  id: number
}

interface Props {
  participation: Participation // Add this prop
  onOrderCreated: (order: PrintOrder) => void
}

const BadgeReprintList: React.FC<Props> = ({ participation, onOrderCreated }) => {
  const [orderedStaff, setOrderedStaff] = useState<OrderedStaff[]>([])
  const [selectedStaff, setSelectedStaff] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [exhibitionFilter, setExhibitionFilter] = useState("all")

  useEffect(() => {
    fetchOrderedStaff(participation.id) // Pass participation.id
  }, [participation.id])

  // Update the fetchOrderedStaff function to accept participationId
  const fetchOrderedStaff = async (participationId: number) => {
    try {
      setLoading(true)
      const data = await BadgeRequestService.getOrderedStaffForReprint(participationId)
      setOrderedStaff(data)
    } catch (error) {
      console.error("Failed to fetch ordered staff:", error)
      toast.error("Failed to load ordered staff")
    } finally {
      setLoading(false)
    }
  }

  const handleStaffToggle = (staffId: number) => {
    setSelectedStaff((prev) => (prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]))
  }

  const calculateTotalAmount = () => {
    return selectedStaff.reduce((total, staffId) => {
      const staff = orderedStaff.find((s) => s.id === staffId)
      return total + (staff?.badge_reprint_price || 0)
    }, 0)
  }

  const handleSubmitReprintOrder = async () => {
    if (selectedStaff.length === 0) {
      toast.error("Please select at least one participant for reprint")
      return
    }

    const totalAmount = calculateTotalAmount()
    const currency = orderedStaff[0]?.currency || "RWF"

    const result = await Swal.fire({
      title: "Confirm Badge Reprint Request",
      html: `
      <div class="text-start p-3">
        <div class="alert alert-warning">
          <h6 class="alert-heading">
          Badge Reprint Request
          </h6>
          <p class="mb-0">You are requesting copy badges for ${selectedStaff.length} participant(s).</p>
        </div>
        
        <div class="card border-primary">
          <div class="card-body">
            <h6 class="card-title text-primary">Payment Information</h6>
            <p><strong>Selected participant:</strong> ${selectedStaff.length}</p>
            <p><strong>Reprint Fee per Badge:</strong> ${orderedStaff[0]?.badge_reprint_price.toLocaleString()} ${currency}</p>
            <p><strong>Total Amount:</strong> 
              <span class="text-primary fw-bold fs-5">${totalAmount.toLocaleString()} ${currency}</span>
            </p>
          </div>
        </div>
        
        <div class="alert alert-info">
          <small>
            <strong>Note:</strong> These are copy badges for participant who already have original badges. 
            Each copy badge requires the reprint fee.
          </small>
        </div>
      </div>
    `,
      icon: "question",
      iconColor: "#0d6efd",
      showCancelButton: true,
      confirmButtonText: `Submit Reprint Request`,
      cancelButtonText: `Cancel`,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      width: "500px",
    })

    if (!result.isConfirmed) return

    try {
      setSubmitting(true)
      const order = await BadgeRequestService.submitBadgeReprintRequest({
        participation_id: participation.id,
        selected_staff: selectedStaff,
      })

      toast.success(
        `Reprint request submitted successfully! Payment of ${totalAmount.toLocaleString()} ${currency} is required.`,
      )

      setTimeout(() => {
        onOrderCreated(order)
      }, 100)
    } catch (error) {
      console.error("Failed to submit reprint order:", error)
      toast.error("Failed to submit badge reprint request")
    } finally {
      setSubmitting(false)
    }
  }

  // Filter staff based on search and exhibition. Guarded with fallbacks
  // because some records can have missing phone/email/company/exhibition
  // values.
  const filteredStaff = orderedStaff.filter((staff) => {
    const term = searchTerm.trim().toLowerCase()
    const matchesSearch =
      !term ||
      [
        `${staff.person?.first_name || ""} ${staff.person?.last_name || ""}`,
        staff.person?.email,
        staff.exhibitor_name,
        staff.exhibition_title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)

    const matchesExhibition = exhibitionFilter === "all" || staff.exhibition_id.toString() === exhibitionFilter

    return matchesSearch && matchesExhibition
  })

  // Get unique exhibitions for filter
  const exhibitions = Array.from(new Set(orderedStaff.map((s) => s.exhibition_id)))
    .map((id) => orderedStaff.find((s) => s.exhibition_id === id))
    .filter(Boolean)

  const totalAmount = calculateTotalAmount()
  const currency = orderedStaff[0]?.currency || "RWF"

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner color="primary" size="lg" />
        <span className="ms-3">Loading ordered participant...</span>
      </div>
    )
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">
                <FaCopy className="me-2 text-primary" />
                Badge Reprint Requests
              </h5>
              <small className="text-muted">Request copy badges for participant who already have original badges</small>
            </div>
            <div className="text-end">
              <Badge color="info" pill>
                {filteredStaff.length} available
              </Badge>
              {selectedStaff.length > 0 && (
                <div className="mt-1">
                  <Badge color="primary" pill>
                    {selectedStaff.length} selected
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardBody>
          {orderedStaff.length === 0 ? (
            <Alert color="info" className="text-center">
              <FaExclamationTriangle className="me-2" />
              <strong>No participant Available for Reprint</strong>
              <div className="mt-2">
                No participant have been found with existing badge orders. Participant must have original badges before
                requesting reprints.
              </div>
            </Alert>
          ) : (
            <>
              {/* Filters */}
              <Row className="mb-4">
                <Col md={6}>
                  <FormGroup>
                    <Label className="fw-semibold">Search Participant</Label>
                    <Input
                      type="text"
                      placeholder="Search by name, email, company, or exhibition..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label className="fw-semibold">Filter by Exhibition</Label>
                    <Input type="select" value={exhibitionFilter} onChange={(e) => setExhibitionFilter(e.target.value)}>
                      <option value="all">All Exhibitions</option>
                      {exhibitions.map((exhibition) => (
                        <option key={exhibition?.exhibition_id} value={exhibition?.exhibition_id}>
                          {exhibition?.exhibition_title}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label className="fw-semibold">Reprint Fee</Label>
                    <div className="form-control-plaintext fw-bold text-primary">
                      {orderedStaff[0]?.badge_reprint_price.toLocaleString()} {currency}
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              {/* Staff List */}
              <Row>
                {filteredStaff.map((staff) => (
                  <Col md={6} lg={4} key={staff.id} className="mb-3">
                    <Card
                      className={`h-100 border-warning ${selectedStaff.includes(staff.id) ? "border-primary bg-light" : ""}`}
                      style={{ fontSize: "0.875rem" }}
                    >
                      <CardBody className="p-3">
                        <FormGroup check className="mb-3">
                          <Input
                            type="checkbox"
                            id={`staff-${staff.id}`}
                            checked={selectedStaff.includes(staff.id)}
                            onChange={() => handleStaffToggle(staff.id)}
                          />
                          <Label check htmlFor={`participant-${staff.id}`} className="ms-2">
                            <strong style={{ fontSize: "0.9rem" }}>
                              {staff.person.first_name} {staff.person.last_name}
                            </strong>
                          </Label>
                        </FormGroup>

                        <div className="mb-3">
                          <Badge color="warning" className="me-2">
                            <FaCopy className="me-1" size={10} />
                            Copy Badge
                          </Badge>
                          <Badge color="success" size="sm">
                            {staff.badge_reprint_price.toLocaleString()} {staff.currency}
                          </Badge>
                        </div>

                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          <div className="d-flex align-items-center mb-1">
                            <FaEnvelope className="me-2" size={10} />
                            <span className="text-truncate">{staff.person.email}</span>
                          </div>
                          <div className="d-flex align-items-center mb-1">
                            <FaPhone className="me-2" size={10} />
                            {staff.person.phone}
                          </div>
                          <div className="d-flex align-items-center mb-1">
                            <FaIdCard className="me-2" size={10} />
                            {staff.person.id_doc_number}
                          </div>
                          {/* <div className="d-flex align-items-center mb-1">
                            <FaUser className="me-2" size={10} />
                            <span className="text-truncate">{staff.role}</span>
                          </div> */}
                          <div className="d-flex align-items-center mb-1">
                            <FaBuilding className="me-2" size={10} />
                            <span className="text-truncate">{staff.exhibitor_name}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <FaCalendar className="me-2" size={10} />
                            <span className="text-truncate">{staff.exhibition_title}</span>
                          </div>

                          <div className="border-top pt-2">
                            <div className="small text-success">
                              <strong>Badge History:</strong>
                            </div>
                            <div className="small">
                              Total Printed: <strong>{staff.total_badges_printed}</strong>
                            </div>
                            <div className="small">
                              Last Type: <strong>{staff.last_badge_type?.toUpperCase()}</strong>
                            </div>
                            {staff.last_printed_at && (
                              <div className="small">
                                Last Printed: <strong>{new Date(staff.last_printed_at).toLocaleDateString()}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {filteredStaff.length === 0 && (
                <Alert color="warning" className="text-center">
                  <strong>No participant found matching your filters</strong>
                  <div>Try adjusting your search terms or exhibition filter.</div>
                </Alert>
              )}

              {/* Payment Summary */}
              {selectedStaff.length > 0 && (
                <Alert color="primary" className="mt-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>
                        <FaCopy className="me-2" />
                        Reprint Summary: {selectedStaff.length} badge(s)
                      </strong>
                      <div className="small text-white opacity-75">
                        Each copy badge costs {orderedStaff[0]?.badge_reprint_price.toLocaleString()} {currency}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="h5 mb-0 text-white">
                        {totalAmount.toLocaleString()} {currency}
                      </div>
                      <div className="small text-white opacity-75">Total Amount</div>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Submit Section */}
              <div className="border-top pt-3 mt-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Selected: {selectedStaff.length} participant(s)</strong>
                    {totalAmount > 0 && (
                      <div className="text-primary">
                        Total Payment Required: {totalAmount.toLocaleString()} {currency}
                      </div>
                    )}
                  </div>
                  <Button
                    color="primary"
                    size="lg"
                    onClick={handleSubmitReprintOrder}
                    disabled={selectedStaff.length === 0 || submitting}
                  >
                    {submitting ? <Spinner size="sm" className="me-2" /> : <FaCopy className="me-2" />}
                    Submit Reprint Request
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default BadgeReprintList
